import { useEffect, useMemo, useState } from 'react'

import * as presentationService from '../services/presentationService'
import type { AssetOption, EntityRef, PresentationScope } from '../types/presentation.types'

/**
 * Names for a set of asset references (MYD-495): the chips on story headers,
 * and the "Draws from" line in the inspector.
 *
 * A deck stores `{ scope, entityId }` and nothing else, deliberately: a name
 * copied into the deck would go stale the day the asset is renamed. So names
 * are looked up, one request per asset type, and cached for the session.
 *
 * An asset the viewer cannot see comes back as `'missing'`, which callers
 * show as "Not available", matching what its blocks will show.
 */

export type AssetName = AssetOption | 'missing' | 'loading'

const keyOf = (ref: EntityRef) => `${ref.scope}:${ref.entityId}`

/** Session cache, shared by every editor instance. */
const cache = new Map<string, AssetOption | 'missing'>()

export function useAssetNames(refs: Array<EntityRef | null | undefined>): (ref: EntityRef | null | undefined) => AssetName | null {
	const [, setVersion] = useState(0)

	const wanted = useMemo(() => {
		const byScope = new Map<PresentationScope, Set<string>>()
		for (const ref of refs) {
			if (!ref || cache.has(keyOf(ref))) continue
			const ids = byScope.get(ref.scope) ?? new Set<string>()
			ids.add(ref.entityId)
			byScope.set(ref.scope, ids)
		}
		return byScope
		// Keyed on content, not identity: the deck hands in new arrays constantly.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refs.map(ref => (ref ? keyOf(ref) : '')).join('|')])

	useEffect(() => {
		let cancelled = false
		for (const [scope, ids] of wanted) {
			const list = [...ids]
			presentationService
				.lookupAssets(scope, list)
				.then(found => {
					const byId = new Map(found.map(asset => [asset.entityId, asset]))
					for (const id of list) cache.set(`${scope}:${id}`, byId.get(id) ?? 'missing')
				})
				// A failed lookup is not cached, so the next render can try again;
				// the chip just keeps showing its scope meanwhile.
				.catch(() => undefined)
				.finally(() => {
					if (!cancelled) setVersion(version => version + 1)
				})
		}
		return () => {
			cancelled = true
		}
	}, [wanted])

	return (ref: EntityRef | null | undefined) => (ref ? (cache.get(keyOf(ref)) ?? 'loading') : null)
}

/** Test seam: forget everything looked up so far. */
export const clearAssetNameCache = () => cache.clear()

export default useAssetNames
